/**
 * What changed about this destination, and when.
 *
 * The watcher sends one notification per change (REQ-013, P4). A notification
 * is read once and gone, so this is the page that still knows — newest first,
 * with the date, because "yellow fever became mandatory" matters differently
 * depending on whether it happened before or after somebody booked.
 */

import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { AdvisoryHistory, getAdvisoryChanges } from "@/services/api/travels";
import { AlertTriangle, Bell, CheckCircle2, ShieldAlert } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

interface AdvisoryChangeLogProps {
  travelId: string;
}

function iconFor(kind: string) {
  if (kind.startsWith("outbreak-over") || kind === "vaccination-dropped") {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />;
  }
  if (kind.startsWith("outbreak")) {
    return <ShieldAlert className="h-4 w-4 shrink-0 text-red-600" />;
  }
  return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />;
}

export const AdvisoryChangeLog: React.FC<AdvisoryChangeLogProps> = ({
  travelId,
}) => {
  const fmt = useLocaleFormat();
  const [data, setData] = useState<AdvisoryHistory | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await getAdvisoryChanges(travelId));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [travelId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="h-24 animate-pulse rounded-xl bg-gray-100" />;
  }

  const entries = data?.history ?? [];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <Bell className="h-5 w-5 text-primary-600" />
        <h3 className="text-base font-semibold text-gray-900">
          Changes to the health advice
        </h3>
      </div>

      {!data?.checkedAt ? (
        <p className="text-sm text-gray-600">
          This destination has not been checked yet. From now on it is checked
          daily, and you are told when the advice changes.
        </p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-600">
          Nothing has changed since this trip was added. Last checked{" "}
          {fmt.date(data.checkedAt)}.
        </p>
      ) : (
        <ol className="space-y-3">
          {entries.map((entry, index) => (
            <li key={`${entry.at}-${index}`} className="text-sm">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                {fmt.date(entry.at)}
              </p>
              <ul className="space-y-1">
                {entry.changes.map((change, changeIndex) => (
                  <li
                    key={`${change.kind}-${changeIndex}`}
                    className="flex items-start gap-2 text-gray-800"
                  >
                    {iconFor(change.kind)}
                    <span>{change.summary}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
